// SHARED-DES in the in-memory API (docs/design/2026-10-30-shared-des-and-fulfillment.md §2.2,
// §3.1; production: services/api/src/domain/org/seat-serves.ts and domain/reach/tasks.ts):
//   - a shared seat's `serves` links (a team, a seat or the company, each with talk / task / see),
//     read by every chart viewer (`GET seats/{id}/serves`, and `service_links` on the org chart);
//   - `PUT seats/{id}/serves`: owners and admins replace the whole link set; `dry_run` returns the
//     review lines (`say`) and writes nothing;
//   - the `task` grant on assign: someone outside the DE's home chain and its served scopes gets
//     the visible refusal `not_in_reach` ("… serves Fulfillment, Sales and CEO. Ask … for access.").
// The seed: Acme's Account Health (home under the Fulfillment Lead) serves Fulfillment, Sales and
// the CEO seat, not Recruiting.
import { seatServesSetVersion } from "@companyos/schema/seat-serves-set";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { COMPANY_IDS, type FakeCompany, SHARED_DE, uid } from "./fake-data";
import type { Seat, SeatAssignment } from "./types";

type View = OperationOutput<"seats.serves">;
type Link = View["links"][number];
type Grants = Link["grants"];
type Kind = Link["target_kind"];
type Change = OperationOutput<"seats.servesUpdate">["changes"][number];
type Team = { id: string; name: string; lead_seat_id: string | null };
type Member = { team_id: string; seat_id: string };

export const SHARED_DES_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "seats.serves",
  "seats.servesUpdate",
]);

const ALL: Grants = { talk: true, task: true, see: true };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const keyOf = (l: { target_kind: string; target_id: string | null }) =>
  `${l.target_kind}:${l.target_id ?? ""}`;
const grantWords = (g: Grants) => {
  const on = (["talk", "task", "see"] as const).filter((k) => g[k]);
  return on.length ? on.join(", ") : "no access";
};
const andList = (xs: string[]) =>
  xs.length > 1 ? `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}` : (xs[0] ?? "");

export type SharedDesDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (
    status: number,
    code:
      | "not_found"
      | "forbidden"
      | "not_in_reach"
      | "validation_failed"
      | "conflict"
      | "precondition_required",
    title: string,
    detail?: string,
    extras?: Record<string, unknown>,
  ) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  now: () => Date;
  seatsOf: (c: FakeCompany) => Seat[];
  assignmentsOf: (c: FakeCompany) => SeatAssignment[];
  teamsOf: (c: FakeCompany) => Team[];
  teamMembersOf: (c: FakeCompany) => Member[];
  nameOf: (c: FakeCompany, membershipId: string) => string;
};

/** Acme's extra seats around the shared DE (the CEO, Sales and Recruiting leads). */
export function sharedDesSeats(
  company: FakeCompany,
  seats: Seat[],
  make: (id: string, title: string, supervisor: string | null) => Seat,
): Seat[] {
  if (company.id !== COMPANY_IDS.acme || !seats.some((s) => s.id === SHARED_DE.deSeat))
    return seats;
  const out = seats.map((s) =>
    s.id === SHARED_DE.fulfillmentLead ? { ...s, supervisor_seat_id: SHARED_DE.ceo } : s,
  );
  out.push(
    { ...make(SHARED_DE.ceo, "CEO", null), status: "open" },
    { ...make(SHARED_DE.salesperson, "Salesperson", SHARED_DE.ceo), status: "open" },
    { ...make(SHARED_DE.recruiter, "Recruiter", SHARED_DE.ceo), status: "open" },
  );
  return out;
}

/** Acme's Fulfillment, Sales and Recruiting teams (lead seat, member seats). */
export const SHARED_DES_TEAMS: { id: string; name: string; lead: string; members: string[] }[] = [
  {
    id: SHARED_DE.teamFulfillment,
    name: "Fulfillment",
    lead: SHARED_DE.fulfillmentLead,
    members: [SHARED_DE.fulfillmentLead, SHARED_DE.deSeat],
  },
  {
    id: SHARED_DE.teamSales,
    name: "Sales",
    lead: SHARED_DE.salesperson,
    members: [SHARED_DE.salesperson],
  },
  {
    id: SHARED_DE.teamRecruiting,
    name: "Recruiting",
    lead: SHARED_DE.recruiter,
    members: [SHARED_DE.recruiter],
  },
];

type Stored = Omit<Link, "target_name">;

export function createSharedDesFake(d: SharedDesDeps) {
  const store = new Map<string, Stored[]>();
  let seq = 0;

  function linksOf(c: FakeCompany): Stored[] {
    let list = store.get(c.id);
    if (!list) {
      list = [];
      if (c.id === COMPANY_IDS.acme && d.seatsOf(c).some((s) => s.id === SHARED_DE.deSeat)) {
        const link = (n: number, kind: Kind, target: string | null): Stored => ({
          id: uid(0x3e50, n),
          seat_id: SHARED_DE.deSeat,
          target_kind: kind,
          target_id: target,
          grants: { ...ALL },
          version: 1,
        });
        list.push(
          link(1, "team", SHARED_DE.teamFulfillment),
          link(2, "team", SHARED_DE.teamSales),
          link(3, "seat", SHARED_DE.ceo),
        );
      }
      store.set(c.id, list);
    }
    return list;
  }

  const targetName = (c: FakeCompany, l: { target_kind: string; target_id: string | null }) =>
    l.target_kind === "company"
      ? null
      : l.target_kind === "team"
        ? (d.teamsOf(c).find((t) => t.id === l.target_id)?.name ?? null)
        : (d.seatsOf(c).find((s) => s.id === l.target_id)?.title ?? null);
  const said = (c: FakeCompany, l: { target_kind: string; target_id: string | null }) =>
    l.target_kind === "company" ? "the whole company" : (targetName(c, l) ?? "a gone target");

  const active = (c: FakeCompany, seatId: string) =>
    d.assignmentsOf(c).filter((a) => a.seat_id === seatId && a.active_to === null);
  /** The seat's DE holder's name, else its title (the review lines' subject). */
  const whoOf = (c: FakeCompany, seat: Seat) => {
    const de = active(c, seat.id).find((a) => a.membership_kind === "digital_employee");
    return de ? d.nameOf(c, de.membership_id) : seat.title;
  };

  /** The caller's active seats in the company. */
  const heldBy = (p: Principal) => {
    const mine = new Set(p.me.memberships.map((m) => m.membership_id));
    return new Set(
      d
        .assignmentsOf(p.company)
        .filter((a) => a.active_to === null && mine.has(a.membership_id))
        .map((a) => a.seat_id),
    );
  };

  /** SHARED-DES-2: the teams `p` may see (null: all), the chart's rule: lead, owner or member. */
  const teamsSeen = (p: Principal): Set<string> | null => {
    if (p.manager) return null;
    const held = heldBy(p);
    const out = new Set<string>();
    for (const t of d.teamsOf(p.company))
      if (t.lead_seat_id && held.has(t.lead_seat_id)) out.add(t.id);
    for (const m of d.teamMembersOf(p.company)) if (held.has(m.seat_id)) out.add(m.team_id);
    return out;
  };
  const hiddenTo = (
    seen: Set<string> | null,
    l: { target_kind: string; target_id: string | null },
  ) => seen !== null && l.target_kind === "team" && !!l.target_id && !seen.has(l.target_id);

  function view(p: Principal, seatId: string): View {
    const mine = linksOf(p.company).filter((l) => l.seat_id === seatId);
    const seen = teamsSeen(p);
    return {
      seat_id: seatId,
      links: mine.map((l) => ({
        ...l,
        target_name: hiddenTo(seen, l) ? null : targetName(p.company, l),
      })),
      editable: p.manager,
      version: seatServesSetVersion(mine),
    };
  }

  /** Whether `p` may hand the shared seat work (`task`): its chain, a manager, a served scope. */
  function mayTask(p: Principal, seatId: string): boolean {
    if (p.manager) return true;
    const held = heldBy(p);
    const seats = d.seatsOf(p.company);
    const seen = new Set<string>();
    for (let at: string | null = seatId; at && !seen.has(at); ) {
      if (held.has(at)) return true;
      seen.add(at);
      at = seats.find((s) => s.id === at)?.supervisor_seat_id ?? null;
    }
    const members = d.teamMembersOf(p.company);
    const teams = d.teamsOf(p.company);
    return linksOf(p.company).some((l) => {
      if (l.seat_id !== seatId || !l.grants.task) return false;
      if (l.target_kind === "company") return true;
      if (l.target_kind === "seat") return l.target_id !== null && held.has(l.target_id);
      const lead = teams.find((t) => t.id === l.target_id)?.lead_seat_id;
      return (
        (lead !== null && lead !== undefined && held.has(lead)) ||
        members.some((m) => m.team_id === l.target_id && held.has(m.seat_id))
      );
    });
  }

  /** The `not_in_reach` refusal for assigning a shared DE's seat, or null when it may. */
  function taskRefusal(p: Principal, seatIds: readonly string[]): Response | null {
    for (const seatId of seatIds) {
      if (!linksOf(p.company).some((l) => l.seat_id === seatId)) continue;
      const de = active(p.company, seatId).find((a) => a.membership_kind === "digital_employee");
      if (!de || mayTask(p, seatId)) continue;
      const seat = d.seatsOf(p.company).find((s) => s.id === seatId);
      // Whom to ask: the people holding the seat above the home seat (the home head).
      const head = seat?.supervisor_seat_id ?? null;
      const ask = (head ? active(p.company, head) : [])
        .filter((a) => a.membership_kind === "person")
        .map((a) => ({
          membership_id: a.membership_id,
          name: d.nameOf(p.company, a.membership_id),
        }));
      const who = ask.length ? ask.map((x) => x.name).join(", ") : "an admin";
      // SHARED-DES-2: a team the caller may not see is never named.
      const seen = teamsSeen(p);
      const mine = linksOf(p.company).filter((l) => l.seat_id === seatId);
      const names = mine
        .filter((l) => !hiddenTo(seen, l))
        .map((l) => (l.target_id ? targetName(p.company, l) : "the whole company"))
        .filter((n): n is string => Boolean(n));
      if (mine.some((l) => hiddenTo(seen, l)))
        names.push(names.length ? "other teams" : "teams you can't see");
      const deName = d.nameOf(p.company, de.membership_id);
      return d.problem(
        403,
        "not_in_reach",
        "Outside the agent's reach",
        `${deName} ${names.length ? `serves ${andList(names)}` : "takes work only from the teams it serves"}. Ask ${who} for access.`,
        { agent_membership_id: de.membership_id, ask },
      );
    }
    return null;
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "seats.serves": ({ principal: p, params }) => {
      if (!p) return d.notFound();
      const seat = d.seatsOf(p.company).find((s) => s.id === params.id);
      if (!seat) return d.notFound();
      return d.json(view(p, seat.id));
    },
    "seats.servesUpdate": ({ principal: p, params, body, headers }) => {
      if (!p) return d.notFound();
      const seat = d.seatsOf(p.company).find((s) => s.id === params.id);
      if (!seat) return d.notFound();
      if (!p.manager) return d.problem(403, "forbidden", "Forbidden");
      const b = (body ?? {}) as {
        links?: { target_kind?: unknown; target_id?: unknown; grants?: Grants }[];
        dry_run?: boolean;
      };
      // SHARED-DES-2: If-Match against the set's version (a dry run may go without it).
      const raw = headers.get("if-match");
      if (!raw && !b.dry_run) return d.problem(428, "precondition_required", "If-Match required");
      if (raw) {
        const m = /^\s*(?:W\/)?"?(\d{1,9})"?\s*$/.exec(raw);
        if (!m) return d.validation('If-Match: expected "{version}"');
        const current = view(p, seat.id);
        if (Number(m[1]) !== current.version)
          return d.problem(409, "conflict", "Conflict", `version is ${current.version}`, {
            current,
          });
      }
      if (!Array.isArray(b.links)) return d.validation("links: required");
      const next: { target_kind: Kind; target_id: string | null; grants: Grants }[] = [];
      const seen = new Set<string>();
      for (const [i, l] of b.links.entries()) {
        const kind = l.target_kind;
        const id = l.target_id ?? null;
        if (kind !== "seat" && kind !== "team" && kind !== "company")
          return d.validation(`links.${i}.target_kind: seat, team or company`);
        if ((kind === "company") !== (id === null))
          return d.validation(
            `links.${i}.target_id: the company has no id; a seat or team needs one`,
          );
        if (id !== null && (typeof id !== "string" || !UUID.test(id)))
          return d.validation(`links.${i}.target_id: a uuid`);
        if (kind === "seat" && id === seat.id)
          return d.validation(`links.${i}.target_id: a seat never serves itself`);
        if (
          kind !== "company" &&
          targetName(p.company, { target_kind: kind, target_id: id }) === null
        )
          return d.validation(`links.${i}.target_id: no such ${kind} in this company`);
        const link = {
          target_kind: kind as Kind,
          target_id: id as string | null,
          grants: l.grants ?? ALL,
        };
        if (seen.has(keyOf(link))) return d.validation(`links.${i}: named twice`);
        seen.add(keyOf(link));
        next.push(link);
      }
      const all = linksOf(p.company);
      const before = all.filter((l) => l.seat_id === seat.id);
      const byKey = new Map(before.map((l) => [keyOf(l), l]));
      const who = whoOf(p.company, seat);
      const changes: Change[] = [];
      for (const l of next) {
        const was = byKey.get(keyOf(l));
        const target = { ...l, target_name: targetName(p.company, l) };
        if (!was)
          changes.push({
            op: "add",
            ...target,
            say: `${who} now serves ${said(p.company, l)}: ${grantWords(l.grants)}`,
          });
        else if (
          was.grants.talk !== l.grants.talk ||
          was.grants.task !== l.grants.task ||
          was.grants.see !== l.grants.see
        )
          changes.push({
            op: "regrant",
            ...target,
            say: `${who} serves ${said(p.company, l)}: ${grantWords(was.grants)} → ${grantWords(l.grants)}`,
          });
      }
      for (const l of before)
        if (!seen.has(keyOf(l)))
          changes.push({
            op: "remove",
            target_kind: l.target_kind,
            target_id: l.target_id,
            target_name: targetName(p.company, l),
            grants: l.grants,
            say: `${who} no longer serves ${said(p.company, l)}`,
          });
      if (b.dry_run || changes.length === 0)
        return d.json({ ...view(p, seat.id), changes, applied: false });
      const kept = all.filter((l) => l.seat_id !== seat.id);
      const written = next.map((l) => {
        const was = byKey.get(keyOf(l));
        if (!was) return { id: uid(0x3e51, ++seq), seat_id: seat.id, ...l, version: 1 };
        const same =
          was.grants.talk === l.grants.talk &&
          was.grants.task === l.grants.task &&
          was.grants.see === l.grants.see;
        return same ? was : { ...was, grants: l.grants, version: was.version + 1 };
      });
      store.set(p.company.id, [...kept, ...written]);
      return d.json({ ...view(p, seat.id), changes, applied: true });
    },
  };

  /** The org chart's `service_links` (every link of the company). */
  const serviceLinks = (c: FakeCompany) =>
    linksOf(c).map((l) => ({
      id: l.id,
      seat_id: l.seat_id,
      target_kind: l.target_kind,
      target_id: l.target_id,
      grants: l.grants,
    }));

  return { handlers, serviceLinks, taskRefusal };
}
